import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, delay, filter, map, switchMap } from 'rxjs/operators'

import { selectIncidentsRedesignEnabled } from 'containers/App/selectors'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from '../../PartnerOptions/constants'
import { MINIMAL_ALLOWED_POLLING_INTERVAL } from '../../Polling/constants'
import { CHANGE_INCIDENT_STATE_REQUEST_ASYNC } from '../AtoIncident/Header/constants'
import { REGULAR_FILTERS_SUBMIT } from '../IncidentFilters/constants'
import {
  selectEffectiveIncidentDateFilter,
  selectRegularFilters,
} from '../IncidentFilters/selectors'
import { QUICK_FILTERS_CHANGE } from '../QuickFilters/constants'
import { selectQuickFilters } from '../QuickFilters/selectors'
import {
  CHANGE_ASSIGNEES_ASYNC,
  CLOSE_INCIDENT_ASYNC,
  CLOSE_SAFE_CHALLENGE_INCIDENT_ASYNC,
  KEEP_ENCRYPTED_INCIDENT_IN_QUARANTINE_ASYNC,
  RELEASE_ENCRYPTED_INCIDENT_ASYNC,
  RELEASE_MSFT_QUARANTINE_REPORT_ASYNC,
  REPORT_STATE_CHANGE_ASYNC,
} from '../Report/constants'
import {
  CHALLENGED_TYPE_FILTER_CHANGE,
  GENERAL_FILTER_CHANGE,
  SEARCH_CHANGE,
  SORTING_FILTER_CHANGE,
} from '../TopBar/constants'
import {
  selectChallengedTypeFilter,
  selectGeneralFilter,
  selectSearch,
  selectSortingBy,
  selectSortingDirection,
} from '../TopBar/selectors'

import { fetchIncidentsList, fetchIncidentsListError, fetchIncidentsListSuccess } from './actions'
import { LIST_REQUEST_ASYNC, PAGINATE_CHANGE, URL_STATE_CHANGE } from './constants'
import { isIncidentsListOnScreen } from './isIncidentsListOnScreen'
import { resolveIncidentListDateFilter } from './resolveIncidentListDateFilter'
import {
  isOnUnclassifed,
  selectFirstPageLoadDate,
  selectLastRowDate,
  selectPage,
  selectUrlState,
} from './selectors'

const fetchIncidentsListEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      LIST_REQUEST_ASYNC.INITIATE,
      REPORT_STATE_CHANGE_ASYNC.SUCCESS,
      CHANGE_INCIDENT_STATE_REQUEST_ASYNC.SUCCESS,
      CHANGE_ASSIGNEES_ASYNC.SUCCESS,
      URL_STATE_CHANGE,
      SEARCH_CHANGE,
      PAGINATE_CHANGE,
      SORTING_FILTER_CHANGE,
      GENERAL_FILTER_CHANGE,
      CHALLENGED_TYPE_FILTER_CHANGE,
      QUICK_FILTERS_CHANGE,
      REGULAR_FILTERS_SUBMIT,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      CLOSE_INCIDENT_ASYNC.SUCCESS,
      CLOSE_SAFE_CHALLENGE_INCIDENT_ASYNC.SUCCESS,
      RELEASE_ENCRYPTED_INCIDENT_ASYNC.SUCCESS,
      RELEASE_MSFT_QUARANTINE_REPORT_ASYNC.SUCCESS,
      KEEP_ENCRYPTED_INCIDENT_IN_QUARANTINE_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (
        // because those actions are not necessary happen on
        // the screen with List component, so no need to refetch
        action.type === QUICK_FILTERS_CHANGE ||
        action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
      ) {
        return isIncidentsListOnScreen(window.location.href)
      } else if (action.type === URL_STATE_CHANGE) {
        return (
          // prevent processing if urlState is null
          // prevent processing while leaving RC
          !!state$.value.resolutioncenter && selectUrlState(state$.value)
        )
      } else if (action.payload?.isPolling) {
        // prevent processing if urlState has changed after polling was scheduled
        return isOnUnclassifed(state$.value) && state$.value.resolutioncenter.list.page === 1
      }
      return true
    }),
    switchMap(() => {
      const state = state$.value

      const incidentsRequest$ = api.resolutionCenter.getIncidentsList(
        selectUrlState(state),
        selectSearch(state),
        selectPage(state),
        selectPage(state) > 1 ? selectFirstPageLoadDate(state) : '',
        selectPage(state) > 1 ? selectLastRowDate(state) || '' : '',
        selectSortingBy(state),
        selectGeneralFilter(state),
        selectQuickFilters(state),
        resolveIncidentListDateFilter({
          urlState: selectUrlState(state),
          incidentsRedesignEnabled: selectIncidentsRedesignEnabled(state),
          dateFilter: selectRegularFilters(state).dateFilter,
          effectiveDateFilter: selectEffectiveIncidentDateFilter(state),
        }),
        selectRegularFilters(state).detectedByFilter,
        selectRegularFilters(state).includesFilter,
        selectRegularFilters(state).excludesFilter,
        selectRegularFilters(state).assignedToFilter.map((e) =>
          e && typeof e === 'object' ? e.id : e
        ),
        selectRegularFilters(state).resolvedFilter,
        selectChallengedTypeFilter(state),
        selectCompanyFilter(state)?.value,
        selectSortingDirection(state),
        selectRegularFilters(state).customDateRange || null,
        selectRegularFilters(state).themisVerdictFilter || [],
        selectRegularFilters(state).challengeSourceFilter || []
      )

      return incidentsRequest$.pipe(
        map((response) => fetchIncidentsListSuccess(response?.response)),
        // 405: transient proxy response with an empty body on the polling request; the app never returns 405 here.
        catchError((error) =>
          of(epicsErrorHandler(fetchIncidentsListError, error, [403, 404, 405]))
        )
      )
    })
  )

const incidentsListPollingEpic = (action$, state$) =>
  action$.pipe(
    ofType(LIST_REQUEST_ASYNC.ERROR, LIST_REQUEST_ASYNC.SUCCESS),
    filter(() => isOnUnclassifed(state$.value) && state$.value.resolutioncenter.list.page === 1),
    switchMap(() =>
      of({}).pipe(
        delay(MINIMAL_ALLOWED_POLLING_INTERVAL),
        map(() => fetchIncidentsList({ isPolling: true }))
      )
    )
  )

export default combineEpics(fetchIncidentsListEpic, incidentsListPollingEpic)
